import { MouseEvent } from 'react';
import {
  AlertModalTypeEnum,
  AutoSave,
  flattenErrors,
  ModelForm,
  useProgrammaticLink,
  useStickySafeNavigate,
  useWarningAlert,
} from '@equifinance/shared';
import styled from 'styled-components';
import PageErrors from '../Errors/PageErrors';
import { stageOnOrAfter } from '../../contexts/helpers';
import { Stages } from '../../types/Stages';
import { useAccountAutoSave, useAccountContext } from '../../contexts';
import ProductSidebarFooter from '../Products/ProductSidebar/ProductSidebarFooter';
import { ApplicationPageType } from '../AccountTabNav';
import AccountStage from '../AccountStage';
import Applicants from './Sections/Applicants';
import SecurityDetails from './Sections/SecurityDetails';
import MortgageSection from './Sections/MortgageSection';
import LoanDetails from './Sections/LoanDetails';
import DebtManagementPlan from './Sections/DebtManagementPlan';
import AdvisorDetailsSection from './Sections/AdvisorDetailsSection';
import useStaticData from './hooks/useStaticData';
import AccountSidebar from './AccountSidebar';
import DirectDebitSection from './Sections/DirectDebitSection';
import DisclosureSection from './Sections/DisclosureSection';
import NextPage from './components/NextPage';
import MonthlyExpenditureSection from './Sections/MonthlyExpenditureSection';
import RentalIncomeExpenditure from './Sections/RentalIncomeExpenditure';
import HouseholdDependentsSection from './Sections/HouseholdDependentsSection';
import IncomeSection from './Sections/IncomeSection';
import AccountTabPage from '../Page/AccountTabPage';

export const ErrorContainer = styled.div` ${({ theme }) => `
  padding-bottom:${theme.padding[4]};
`}`;

export default function AccountPage() {
  const {
    account, updateAccount, creditSearchCompleted,
  } = useAccountContext();

  const {
    errors, warnings, stage, applicationNumber,
  } = account ?? {};

  const flattenedErrors = flattenErrors(errors);
  const flattenedWarnings = flattenErrors(warnings);

  const staticData = useStaticData();

  const {
    industries,
    jobTitles,
    countries,
    ukCountries,
    lendingRegions,
    accountancyQualifications,
  } = staticData;

  const update = (values: Record<string, any>, disableIncrementUpdateCount: boolean = false) => {
    Object.entries(values).forEach(([field, value]) => {
      updateAccount(field, value, disableIncrementUpdateCount);
    });
  };

  const navigate = useStickySafeNavigate();
  const clickHomeLink = useProgrammaticLink('/');

  const warningAlert = useWarningAlert();

  const onClickLogo = (event?: MouseEvent<HTMLElement>) => {
    if (applicationNumber) {
      clickHomeLink(event);
    } else {
      warningAlert({
        onConfirm: () => { navigate('/'); },
        title: 'Leave application',
        text: 'Your content has not been saved yet.',
        content: 'Are you sure you want to leave this page?',
        confirmLabel: 'Leave',
        type: AlertModalTypeEnum.WARNING_IRREVERSIBLE,
      });
    }
  };

  return (
    <AccountTabPage
      subHeader={<AccountStage />}
      sidebar={<AccountSidebar />}
      sidebarFooter={<ProductSidebarFooter />}
      onClickLogo={onClickLogo}
      page={ApplicationPageType.CASE_DETAILS}
    >
      <PageErrors id={account?.id} errors={flattenedErrors} warnings={flattenedWarnings} />
      <AutoSave autoSave={useAccountAutoSave}>
        <ModelForm model={account} update={update} errors={errors} warnings={warnings}>
          <Applicants
            countries={countries}
            lendingRegions={lendingRegions}
            ukCountries={ukCountries}
            accountancyQualifications={accountancyQualifications}
            industries={industries}
            jobTitles={jobTitles}
          />

          {(stage === Stages.Affordability) && (
            <IncomeSection />
          )}

          {stageOnOrAfter(stage, Stages.Affordability) && (
            <>
              <HouseholdDependentsSection />
              <MonthlyExpenditureSection />
            </>
          )}

          {stageOnOrAfter(stage, Stages.Packaging) && (
            <RentalIncomeExpenditure countries={ukCountries} />
          )}

          <SecurityDetails
            ukCountries={ukCountries}
            lendingRegions={lendingRegions}
          />

          {stageOnOrAfter(stage, Stages.Packaging) && (
            <>
              <DisclosureSection />
              <DirectDebitSection />
            </>
          )}

          {(stage === Stages.CreditDecision) && (
            <MortgageSection />
          )}

          {!creditSearchCompleted && (
            <LoanDetails />
          )}

          <DebtManagementPlan />

          <AdvisorDetailsSection
            countries={countries}
          />
        </ModelForm>
      </AutoSave>
      <NextPage />
    </AccountTabPage>
  );
}
